import { assertNever } from 'notable-common';
import { z } from 'zod';
import { FlowBlockValueTemplateHint, validateFlowBlockValueTemplate } from './template.js';
export var FlowBlockValueFieldSchemaVariant;
(function (FlowBlockValueFieldSchemaVariant) {
    FlowBlockValueFieldSchemaVariant["TemplateAndPlainString"] = "TemplateAndPlainString";
    FlowBlockValueFieldSchemaVariant["TemplateOnly"] = "TemplateOnly";
    FlowBlockValueFieldSchemaVariant["OptionalTemplate"] = "OptionalTemplate";
})(FlowBlockValueFieldSchemaVariant || (FlowBlockValueFieldSchemaVariant = {}));
/**
 * A Zod schema for validating block value template strings with variant fallback to plain string.
 * e.g., `{{blockName.data.field}}`
 *
 * The schema validates strings based on the provided `variant`:
 * - `TemplateAndPlainString` <Default>: Accepts both valid template strings and plain strings.
 * - `TemplateOnly`: Accepts only valid template strings.
 * - `OptionalTemplate`: Accepts valid template strings or an empty string.
 */
export function FlowBlockValueFieldSchema(props) {
    return z.string().superRefine((value, context) => {
        const validationResult = validateFlowBlockValueTemplate(value);
        if (validationResult.hint === FlowBlockValueTemplateHint.Valid) {
            return;
        }
        if ('message' in validationResult) {
            context.addIssue({
                code: 'custom',
                message: validationResult.message,
            });
        }
        if (validationResult.hint === FlowBlockValueTemplateHint.NotTemplateLike) {
            const { variant = FlowBlockValueFieldSchemaVariant.TemplateAndPlainString } = props ?? {};
            if (variant === FlowBlockValueFieldSchemaVariant.OptionalTemplate && value === '') {
                return;
            }
            switch (variant) {
                case FlowBlockValueFieldSchemaVariant.TemplateAndPlainString:
                    return;
                case FlowBlockValueFieldSchemaVariant.OptionalTemplate:
                case FlowBlockValueFieldSchemaVariant.TemplateOnly:
                    context.addIssue({
                        code: 'custom',
                        message: 'Must be a valid interpolated variable. e.g. {{blockName.data.field}}',
                    });
                    return;
                default:
                    assertNever('Unknown BlockValueFieldSchemaVariant', variant);
            }
        }
    });
}
export function FlowBlockValueFieldSchemaV2(props) {
    const { variant } = props;
    const blockValueSchema = z.string().superRefine((value, context) => {
        const validationResult = validateFlowBlockValueTemplate(value);
        if (validationResult.hint === FlowBlockValueTemplateHint.Valid) {
            return z.NEVER;
        }
        if ('message' in validationResult) {
            context.addIssue({
                code: 'custom',
                message: validationResult.message,
            });
            return z.NEVER;
        }
        context.addIssue({
            code: 'custom',
            message: 'Must be a valid interpolated variable. e.g. {{blockName.data.field}}',
        });
        return z.NEVER;
    });
    if (variant === FlowBlockValueFieldSchemaVariant.TemplateOnly) {
        return blockValueSchema;
    }
    return z.union([blockValueSchema, z.literal('')]);
}
//# sourceMappingURL=fieldSchema.js.map